import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Breadcrumbs,
	Button,
	CircularProgress,
	Divider,
	Link,
	Paper,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import { ChevronRight, ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DealerOverview from "./DealerOverview";
import DealerInsights from "./DealerInsights";
import { fetchDealersList } from "../../api/dealers";

const TAB_DEFS = [
	{ key: "overview", label: "Overview" },
	{ key: "insights", label: "Insights" },
];

const tabIndexFromKey = (key) =>
	TAB_DEFS.findIndex((tab) => tab.key === key);

const DEFAULT_TAB = 0;

export default function DealerPage() {
	const { dealerId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const dealerFromState = location.state?.dealer ?? null;
	const [dealer, setDealer] = useState(dealerFromState);
	const [loading, setLoading] = useState(!dealerFromState);
	const [error, setError] = useState(null);
	const [tabIndex, setTabIndex] = useState(DEFAULT_TAB);

	const activeTab = useMemo(() => TAB_DEFS[tabIndex] ?? TAB_DEFS[DEFAULT_TAB], [tabIndex]);

	useEffect(() => {
		if (!dealerId) return;
		const hash = (location.hash || "").replace("#", "").toLowerCase();
		const idx = tabIndexFromKey(hash);
		if (idx >= 0) setTabIndex(idx);
	}, [location.hash, dealerId]);

	useEffect(() => {
		if (dealerFromState) {
			setDealer(dealerFromState);
			setLoading(false);
			setError(null);
		}
	}, [dealerFromState]);

	useEffect(() => {
		if (!dealerId) return;
		if (dealerFromState) return;
		const controller = new AbortController();
		setLoading(true);
		setError(null);
		fetchDealersList(controller.signal)
			.then((list) => {
				const match = (Array.isArray(list) ? list : []).find(
					(entry) => String(entry?.id ?? entry?._id) === String(dealerId)
				);
				if (match) {
					setDealer(match);
					setError(null);
				} else {
					setDealer(null);
					setError("Dealer not found.");
				}
			})
			.catch((err) => {
				if (controller.signal.aborted) return;
				setDealer(null);
				setError(err?.message || "Unable to load dealer details");
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [dealerId, dealerFromState]);

	const handleTabChange = (_, newValue) => {
		setTabIndex(newValue);
		const key = TAB_DEFS[newValue]?.key;
		if (key) {
			navigate(`#${key}`, { replace: true });
		}
	};

	const handleBackClick = () => {
		navigate("/vehicles", { replace: false });
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "#f5f5f5",
				p: { xs: 1.5, md: 3 },
			}}
		>
			<Box
				sx={{
					maxWidth: 1280,
					mx: "auto",
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Button
						onClick={handleBackClick}
						startIcon={<ArrowBack fontSize="small" />}
						variant="text"
						size="small"
					>
						Back to Fleet
					</Button>
					<Box>
						<Breadcrumbs separator={<ChevronRight fontSize="small" />}>
							<Link
								color="inherit"
								underline="hover"
								onClick={() => navigate("/vehicles")}
								sx={{ cursor: "pointer" }}
							>
								Fleet
							</Link>
							<Typography color="text.primary">
								{dealer?.name ?? `Dealer ${dealerId}`}
							</Typography>
						</Breadcrumbs>
					</Box>
				</Box>
				<Paper
					sx={{
						p: { xs: 2, md: 3 },
						borderRadius: 3,
						boxShadow: 4,
						minHeight: 420,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography variant="h4" fontWeight={700}>
							{dealer?.name ?? "Dealer"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							ID #{dealerId}
						</Typography>
					</Box>
					<Divider />
					<Tabs
						value={tabIndex}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto"
						textColor="primary"
						indicatorColor="primary"
						sx={{
							borderBottom: 1,
							borderColor: "divider",
						}}
					>
						{TAB_DEFS.map((tab) => (
							<Tab key={tab.key} label={tab.label} />
						))}
					</Tabs>
					<Box sx={{ flexGrow: 1, mt: 2 }}>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									minHeight: 320,
								}}
							>
								<CircularProgress />
							</Box>
						) : error ? (
							<Box sx={{ p: 3 }}>
								<Typography color="error">{error}</Typography>
							</Box>
						) : activeTab.key === "overview" ? (
							<DealerOverview dealer={dealer} dealerId={dealerId} />
						) : (
							<DealerInsights dealerId={dealerId} dealerName={dealer?.name} />
						)}
					</Box>
				</Paper>
			</Box>
		</Box>
	);
}
